import React, {useContext} from 'react';

import {ChangeLogInitiatorCell} from 'src/components/change-log';
import {UserLink} from 'src/components/links';

import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import Table, {Body, Cell, Row} from '@pb/kit-component-table';
import {LocaleContext} from '@pb/kit-context-locale';

import {SECTION_NAME} from './constants';
import {McpLimitLogPagination} from './mcp-limit-log-pagination';
import {McpLimitLogTableHeader} from './mcp-limit-log-table-header';
import {
    formatUtcDate,
    getExpirationChange,
    getLimitChange,
    isUserEvent,
} from './utils';


import {mcpLimitAuditLogPagination} from './mcp-limit-audit-log.module.styl';

import type {McpLimitLogEvent} from './utils';
import type {AccountMcpLimitLogHits} from 'src/openapi/admin/models/AccountMcpLimitLogHits';
import type {McpLimitType} from 'src/openapi/admin/models/McpLimitType';
import type {SortDirection} from 'src/openapi/admin/models/SortDirection';
import type {UserMcpLimitLogHits} from 'src/openapi/admin/models/UserMcpLimitLogHits';


type Props = {
    rows: McpLimitLogEvent[],
    totalCount: number,

    limitTypes?: McpLimitType[],
    startDate?: string,
    endDate?: string,

    onLimitTypesChange: (values?: McpLimitType[]) => void,
    onDateFilterChange: (startDate: string | undefined, endDate: string | undefined) => void,

    onSort: (direction: 'ASC' | 'DESC') => void,
    sortDirection: SortDirection,
};

export const McpLimitLogTable = ({
    rows, totalCount,
    limitTypes, startDate, endDate,
    onLimitTypesChange, onDateFilterChange,
    onSort, sortDirection,
}: Props) => {
    const {locale} = useContext(LocaleContext);

    const {dateFormat} = locale;

    return (
        <>
            <Table borderedGrid={true}>
                <McpLimitLogTableHeader
                    limitTypes={limitTypes}
                    startDate={startDate}
                    endDate={endDate}
                    onLimitTypesChange={onLimitTypesChange}
                    onDateFilterChange={onDateFilterChange}
                    sortDirection={sortDirection}
                    onSort={onSort}
                />
                <Body>
                    {rows.map(event => {
                        const limitChange = getLimitChange(event);
                        const expirationChange = getExpirationChange(event);
                        const userEvent = isUserEvent(event);

                        return (
                            <Row key={event.recordId}>
                                <Cell>
                                    {userEvent && (event as UserMcpLimitLogHits).targetUserId ? (
                                        <UserLink
                                            userId={(event as UserMcpLimitLogHits).targetUserId!}
                                            caption={
                                                (event as UserMcpLimitLogHits).targetUserEmail || ''
                                            }
                                            place={SECTION_NAME}
                                        />
                                    ) : (
                                        <EllipsisTextHint
                                            title={
                                                (event as AccountMcpLimitLogHits).targetAccountName
                                                || ''
                                            }>
                                            {(event as AccountMcpLimitLogHits).targetAccountName
                                                || ''}
                                        </EllipsisTextHint>
                                    )}
                                </Cell>
                                <Cell>
                                    <EllipsisTextHint title={limitChange?.fieldName || ''}>
                                        {limitChange?.fieldName || ''}
                                    </EllipsisTextHint>
                                </Cell>
                                <Cell>{limitChange?.oldValue || '--'}</Cell>
                                <Cell>{limitChange?.newValue || '--'}</Cell>
                                <Cell>
                                    {expirationChange?.oldValue ? (
                                        <span title={formatUtcDate(expirationChange.oldValue, dateFormat, 'DATE_TIME')}>
                                            {formatUtcDate(expirationChange.oldValue, dateFormat, 'DATE')}
                                        </span>
                                    ) : '--'}
                                </Cell>
                                <Cell>
                                    {expirationChange?.newValue ? (
                                        <span title={formatUtcDate(expirationChange.newValue, dateFormat, 'DATE_TIME')}>
                                            {formatUtcDate(expirationChange.newValue, dateFormat, 'DATE')}
                                        </span>
                                    ) : '--'}
                                </Cell>
                                <Cell>
                                    <ChangeLogInitiatorCell
                                        user={event.initiatorUser}
                                        system={event.initiatorSystem}
                                        process={event.initiatorSystemProcess}
                                        place={SECTION_NAME}
                                    />
                                </Cell>
                                <Cell>
                                    <span title={formatUtcDate(event.timestamp || '', dateFormat, 'DATE_TIME')}>
                                        {formatUtcDate(event.timestamp || '', dateFormat, 'DATE')}
                                    </span>
                                </Cell>
                            </Row>
                        );
                    })}
                </Body>
            </Table>
            <div className={mcpLimitAuditLogPagination}>
                <McpLimitLogPagination totalCount={totalCount}/>
            </div>
        </>
    );
};
